import React, { useState, useEffect } from 'react';
import Input from '../Common/Input';
import Select from '../Common/Select';
import Textarea from '../Common/Textarea';
import Button from '../Common/Button';
import api from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { showSuccess, showError } from '../../utils/swal';

const RentalForm = ({ portfolio, onClose, onSuccess }) => {
  const { user } = useAuth();
  const isManager = user?.is_manager || user?.role === 'office_owner';
  const [customers, setCustomers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_id: portfolio?.option_type === 'customer' && portfolio?.option_customer_id
      ? portfolio.option_customer_id : '',
    user_id: isManager ? '' : (user?.id || ''),
    rental_price: portfolio?.list_price || '',
    deposit_amount: '',
    start_date: '',
    end_date: '',
    contract_duration_months: 12,
    payment_period: 'monthly',
    commission_amount: '',
    commission_payment_date: '',
    notes: '',
  });

  useEffect(() => {
    if (isManager) fetchUsers();
    fetchCustomers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      setUsers([]);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data.data || response.data);
    } catch (error) {
      console.error('Müşteriler yüklenemedi:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Calculate end date if start date or duration changes
    if (name === 'start_date' || name === 'contract_duration_months') {
      const startDate = name === 'start_date' ? value : formData.start_date;
      const months = name === 'contract_duration_months' ? parseInt(value) : parseInt(formData.contract_duration_months);
      
      if (startDate && months) {
        const endDate = new Date(startDate);
        endDate.setMonth(endDate.getMonth() + months);
        setFormData(prev => ({
          ...prev,
          [name]: value,
          end_date: endDate.toISOString().split('T')[0],
        }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/rentals', {
        ...formData,
        portfolio_id: portfolio.id,
      });

      showSuccess('Kiralama başarıyla oluşturuldu');
      onSuccess();
    } catch (error) {
      console.error('Kiralama oluşturulamadı:', error);
      showError(error.response?.data?.message || 'Kiralama oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {isManager ? (
          <Select
            label="İşlemi Yapan Görevli"
            name="user_id"
            value={formData.user_id}
            onChange={handleChange}
            options={users.map(u => ({
              value: u.id,
              label: u.name
            }))}
          />
        ) : (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">İşlemi Yapan Görevli</label>
            <p className="px-3 py-2 bg-gray-100 border border-gray-200 rounded-md text-gray-700">{user?.name}</p>
          </div>
        )}

        <Select
          label="Müşteri *"
          name="customer_id"
          value={formData.customer_id}
          onChange={handleChange}
          required
          options={[
            { value: '', label: 'Müşteri seçiniz...' },
            ...customers.map(c => ({
              value: c.id,
              label: `${c.name} ${c.surname}${c.phone ? ' - ' + c.phone : ''}`
            }))
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Kira Bedeli (₺) *"
          name="rental_price"
          type="number"
          step="0.01"
          value={formData.rental_price}
          onChange={handleChange}
          required
        />

        <Input
          label="Depozito Tutarı (₺)"
          name="deposit_amount"
          type="number"
          step="0.01"
          value={formData.deposit_amount}
          onChange={handleChange}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Başlangıç Tarihi *"
          name="start_date"
          type="date"
          value={formData.start_date}
          onChange={handleChange}
          required
        />

        <Input
          label="Bitiş Tarihi *"
          name="end_date"
          type="date"
          value={formData.end_date}
          onChange={handleChange}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Sözleşme Süresi (Ay) *"
          name="contract_duration_months"
          type="number"
          min="1"
          value={formData.contract_duration_months}
          onChange={handleChange}
          required
        />

        <Select
          label="Ödeme Periyodu *"
          name="payment_period"
          value={formData.payment_period}
          onChange={handleChange}
          required
          options={[
            { value: 'monthly', label: 'Aylık' },
            { value: 'quarterly', label: '3 Aylık' },
            { value: 'semi_annual', label: '6 Aylık' },
            { value: 'annual', label: 'Yıllık' },
          ]}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Komisyon Tutarı (₺)"
          name="commission_amount"
          type="number"
          step="0.01"
          value={formData.commission_amount}
          onChange={handleChange}
          placeholder="Komisyon tutarını giriniz"
        />

        <Input
          label="Komisyon Ödeme Tarihi"
          name="commission_payment_date"
          type="date"
          value={formData.commission_payment_date}
          onChange={handleChange}
        />
      </div>

      <Textarea
        label="Notlar"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        rows={3}
        placeholder="Kiralama ile ilgili notlar..."
      />

      <div className="flex gap-3 justify-end pt-4">
        <Button
          type="button"
          variant="secondary"
          onClick={onClose}
        >
          İptal
        </Button>
        <Button
          type="submit"
          disabled={loading}
        >
          {loading ? 'Kaydediliyor...' : 'Kiralamayı Tamamla'}
        </Button>
      </div>
    </form>
  );
};

export default RentalForm;
